import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, X, AlertCircle, Info, CheckCircle, Clock, Send, Users, Image as ImageIcon, Trash2, Power, ChevronLeft, ChevronRight, ExternalLink, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api, { getFileUrl } from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import Modal from '../../components/Common/Modal';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Textarea from '../../components/Common/Textarea';
import Select from '../../components/Common/Select';

import { useTheme } from '../../context/ThemeContext';

const Notifications = () => {
    const { user } = useAuth();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });

    // Broadcast State (Admin Only)
    const [showBroadcastModal, setShowBroadcastModal] = useState(false);
    const [broadcastData, setBroadcastData] = useState({
        targetRole: 'student',
        title: '',
        message: '',
        type: 'info'
    });
    const [sending, setSending] = useState(false);

    // Banner State
    const [showBannerModal, setShowBannerModal] = useState(false);
    const [bannerData, setBannerData] = useState({
        image: null,
        message: ''
    });
    const [savingBanner, setSavingBanner] = useState(false);

    // View State
    const [view, setView] = useState('notifications'); // 'notifications' | 'banners'
    const [banners, setBanners] = useState([]);
    const [loadingBanners, setLoadingBanners] = useState(false);

    useEffect(() => {
        if (view === 'banners') {
            fetchAdminBanners();
        }
    }, [view]);

    const fetchAdminBanners = async () => {
        if (!user?.college) return;
        const collegeId = typeof user.college === 'object' ? user.college._id : user.college;
        try {
            setLoadingBanners(true);
            const { data } = await api.get(`/v1/college-banners/college/${collegeId}?all=true`);
            if (data.success) {
                setBanners(data.data || []);
            }
        } catch (error) {
            toast.error('Failed to fetch banners');
        } finally {
            setLoadingBanners(false);
        }
    };

    const toggleBannerStatus = async (bannerId, currentStatus) => {
        try {
            setBanners(prev => prev.map(b => b._id === bannerId ? { ...b, isActive: !currentStatus } : b));
            await api.put(`/v1/college-banners/${bannerId}`, { isActive: !currentStatus });
            toast.success('Banner status updated');
        } catch (error) {
            toast.error('Failed to update status');
            fetchAdminBanners();
        }
    };

    const handleDeleteBanner = async (bannerId) => {
        if (!window.confirm('Are you sure you want to delete this banner?')) return;
        try {
            setBanners(prev => prev.filter(b => b._id !== bannerId));
            await api.delete(`/v1/college-banners/${bannerId}`);
            toast.success('Banner deleted');
        } catch (error) {
            toast.error('Failed to delete banner');
            fetchAdminBanners();
        }
    };

    useEffect(() => {
        fetchNotifications();
        // Optional: Polling every 60 seconds
        const interval = setInterval(() => fetchNotifications(false), 60000);
        return () => clearInterval(interval);
    }, [pagination.page, pagination.limit, filter]);

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleLimitChange = (newLimit) => {
        setPagination(prev => ({ ...prev, limit: parseInt(newLimit), page: 1 }));
    };

    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
    }, [filter]);

    const fetchNotifications = async (showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            const { data } = await api.get('/notifications', {
                params: {
                    page: pagination.page,
                    limit: pagination.limit,
                    filter: filter
                }
            });
            if (data.success) {
                setNotifications(data.data);
                setPagination(prev => ({
                    ...prev,
                    total: data.total,
                    totalPages: data.totalPages
                }));
            }
        } catch (error) {
            console.error('Fetch notifications error', error);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            // Optimistic update
            setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
            await api.put(`/notifications/${id}/read`);
        } catch (error) {
            toast.error('Failed to update');
            fetchNotifications(); // Revert on error
        }
    };

    const markAllAsRead = async () => {
        try {
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            await api.put('/notifications/all/read');
            toast.success('Marked all as read');
        } catch (error) {
            toast.error('Failed to update');
        }
    };

    const deleteNotification = async (id) => {
        try {
            setNotifications(prev => prev.filter(n => n._id !== id));
            await api.delete(`/notifications/${id}`);
            toast.success('Deleted');
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const resolveNotificationLink = (rawLink) => {
        if (!rawLink) return null;
        const role = user?.role;
        const isStaff = ['admin', 'support', 'office_admin'].includes(role);
        const isAdmin  = role === 'admin' || role === 'office_admin';
        const isCollege = role === 'college_admin';

        const VALID_ROUTES = new Set([
            '/dashboard', '/profile', '/settings', '/notifications',
            '/admin/dashboard', '/admin/student-onboarding', '/admin/add-recruiter',
            '/admin/recruiters', '/admin/email-tester', '/admin/notifications',
            '/admin/career-analytics', '/admin/proctor', '/admin/grievances',
            '/admin/community', '/admin/activity-log', '/admin/access-logs',
            '/college/dashboard', '/college/departments', '/college/degrees',
            '/college/students', '/college/placement',
            '/college/recruiter', '/college/career-analytics', '/college/courses',
            '/college/analytics', '/college/calendar', '/college/reports',
            '/college/notifications', '/college/student-progress',
            '/student/dashboard', '/student/courses', '/student/my-courses',
            '/student/passport', '/student/micro-assessments',
            '/recruiter/dashboard', '/recruiter/jobs', '/recruiter/applicants',
            '/colleges', '/colleges/onboard', '/manage-batches',
            '/users', '/users/create',
            '/courses', '/courses/create',
            '/assessments', '/assessments/create',
            '/analytics', '/analytics/dashboard',
            '/it-support', '/my-it-support',
            '/support/dashboard', '/support',
            '/case-log', '/security/sessions', '/institute-chain',
        ]);

        const keywordRoutes = [
            { test: /support|ticket/i, resolve: () => {
                const id = rawLink.match(/([a-f0-9]{24})/i)?.[1];
                if (id) return isStaff ? `/support/${id}` : `/my-it-support`;
                return isStaff ? `/support/dashboard` : `/my-it-support`;
            }},
            { test: /career/i, resolve: () =>
                isAdmin ? `/admin/career-analytics` : isCollege ? `/college/career-analytics` : `/dashboard`
            },
            { test: /community|post|announcement/i, resolve: () =>
                isAdmin ? `/admin/community` : isCollege ? `/college/notifications` : `/notifications`
            },
            { test: /grievance/i, resolve: () =>
                isAdmin ? `/admin/grievances` : `/notifications`
            },
            { test: /assessment|result/i, resolve: () => {
                const id = rawLink.match(/([a-f0-9]{24})/i)?.[1];
                if (id && isAdmin) return `/assessments/view/${id}`;
                return `/notifications`;
            }},
            { test: /proctor/i, resolve: () =>
                isAdmin ? `/admin/proctor` : `/notifications`
            },
            { test: /access.log|audit.log|activity.log/i, resolve: () =>
                isAdmin ? `/admin/access-logs` : `/notifications`
            },
            { test: /\/courses?\//, resolve: () => {
                const id = rawLink.match(/([a-f0-9]{24})/i)?.[1];
                if (id && isAdmin) return `/courses/${id}`;
                if (id && isCollege) return `/college/courses`;
                return `/notifications`;
            }},
            { test: /college|institute/i, resolve: () => {
                const id = rawLink.match(/([a-f0-9]{24})/i)?.[1];
                if (id && isAdmin) return `/colleges/${id}`;
                return isCollege ? `/college/dashboard` : `/dashboard`;
            }},
            { test: /\/users?\//, resolve: () => {
                const id = rawLink.match(/([a-f0-9]{24})/i)?.[1];
                if (id && isAdmin) return `/users/${id}`;
                return isAdmin ? `/users` : `/dashboard`;
            }},
            { test: /student/i, resolve: () =>
                isAdmin ? `/admin/student-onboarding` : isCollege ? `/college/students` : `/student/dashboard`
            },
        ];

        for (const { test, resolve } of keywordRoutes) {
            if (test.test(rawLink)) return resolve();
        }

        if (rawLink.startsWith('/')) {
            const basePath = rawLink.split('?')[0].split('#')[0];
            if (VALID_ROUTES.has(basePath)) return rawLink;
            const dynamicPrefixes = [
                '/support/', '/users/', '/colleges/', '/courses/', '/assessments/',
                '/college/students/', '/institute-chain/',
                '/student/courses/', '/student/micro-assessments/',
                '/admin/student-onboarding/', '/admin/edit-recruiter/',
            ];
            if (dynamicPrefixes.some(p => basePath.startsWith(p))) return rawLink;
        }

        return isAdmin ? `/admin/dashboard` : isCollege ? `/college/dashboard` : `/dashboard`;
    };

    const handleNotificationClick = async (n) => {
        if (!n.read) {
            await markAsRead(n._id);
        }
        const resolved = resolveNotificationLink(n.link);
        if (resolved) navigate(resolved);
    };


    const handleBroadcastSubmit = async () => {
        if (!broadcastData.title || !broadcastData.message) {
            toast.error('Please fill in all fields');
            return;
        }

        try {
            setSending(true);
            const { data } = await api.post('/notifications/send', broadcastData);
            if (data.success) {
                toast.success(data.message);
                setShowBroadcastModal(false);
                setBroadcastData({ targetRole: 'student', title: '', message: '', type: 'info' });
                fetchNotifications(); // Refresh to see sent? (Actually sender usually doesn't need to see it unless self-cc)
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to send notification');
        } finally {
            setSending(false);
        }
    };

    const handleBannerSubmit = async () => {
        if (!bannerData.image) {
            toast.error('Please select an image for the banner');
            return;
        }

        try {
            setSavingBanner(true);
            
            // First upload image
            const formData = new FormData();
            formData.append('image', bannerData.image);
            
            const uploadRes = await api.post('/upload/image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (!uploadRes.data.success) {
                throw new Error('Image upload failed');
            }

            // Then save banner
            const bannerPayload = {
                image: uploadRes.data.url || uploadRes.data.data.url || uploadRes.data.data, // depending on how the backend formats it
                message: bannerData.message
            };

            const { data } = await api.post('/v1/college-banners', bannerPayload);
            if (data.success) {
                toast.success('Institute banner added successfully');
                setShowBannerModal(false);
                setBannerData({ image: null, message: '' });
                if (view === 'banners') fetchAdminBanners();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'Failed to add banner');
        } finally {
            setSavingBanner(false);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'warning': return <AlertCircle className="w-5 h-5 text-yellow-500" />;
            case 'error': return <X className="w-5 h-5 text-red-500" />;
            default: return <Info className="w-5 h-5 text-blue-500" />;
        }
    };

    const getTimeAgo = (dateStr) => {
        const date = new Date(dateStr);
        const seconds = Math.floor((new Date() - date) / 1000);
        if (seconds < 60) return 'Just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        return format(date, 'MMM dd');
    };

    const unreadCount = notifications.filter(n => !n.read).length; // This reflects the current page's unread count, or we could fetch total unread from backend

    const canBroadcast = ['admin', 'college_admin'].includes(user?.role);

    const getTargetOptions = () => {
        const role = user?.role;
        const opts = [
            { value: 'student', label: 'My Students' }
        ];

        if (role === 'admin') {
            return [
                { value: 'student', label: 'All Students' },
                { value: 'college_admin', label: 'All Institutes' },
                { value: 'all', label: 'Everyone (System Wide)' }
            ];
        }

        return opts;
    };

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            {/* Header Section */}
            <div className="border border-gray-100 dark:border-[#1a3884]/20 rounded-[1.25rem] px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#002147]">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/dashboard')} className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="space-y-0.5 text-left">
                        <h1 className="text-[22px] font-black text-[#1a3884] dark:text-[#4f7dff] tracking-tight">
                            Notifications
                        </h1>
                        <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400">
                            <Bell className="w-4 h-4 text-[#1a3884]/60 dark:text-[#4f7dff]/60" />
                            Updates and announcements
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    {canBroadcast && (
                        <>
                            <button
                                onClick={() => setShowBannerModal(true)}
                                className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold bg-[#1a3884] dark:bg-[#4f7dff] text-white rounded-xl shadow-md transition-all hover:bg-[#132A55] dark:hover:bg-[#3b66db] active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:ring-offset-2"
                            >
                                <ImageIcon className="w-4 h-4 text-white" />
                                Add Banner
                            </button>
                            <Button
                                variant="primary"
                                icon={Send}
                                onClick={() => setShowBroadcastModal(true)}
                                className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold bg-[#1a3884] hover:bg-[#132A55] active:scale-95 text-white rounded-xl shadow-md transition-all"
                            >
                                Broadcast
                            </Button>
                        </>
                    )}
                    {unreadCount > 0 && (
                        <Button
                            variant="outline"
                            onClick={markAllAsRead}
                            className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-700 dark:text-gray-200 rounded-xl transition-all bg-white dark:bg-[#001f40]"
                        >
                            Mark all read
                        </Button>
                    )}
                </div>
            </div>

            {/* Main Tabs */}
            {canBroadcast && (
                <div className="flex gap-6 border-b border-gray-200 dark:border-[#1a3884]/30 mb-2">
                    <button
                        onClick={() => setView('notifications')}
                        className={`pb-3 text-sm font-medium transition-colors border-b-2 ${view === 'notifications' ? 'text-[#1a3884] border-[#1a3884] dark:text-[#4f7dff] dark:border-[#4f7dff]' : 'text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300'}`}
                    >
                        Announcements
                    </button>
                    <button
                        onClick={() => setView('banners')}
                        className={`pb-3 text-sm font-medium transition-colors border-b-2 ${view === 'banners' ? 'text-[#1a3884] border-[#1a3884] dark:text-[#4f7dff] dark:border-[#4f7dff]' : 'text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300'}`}
                    >
                        Manage Banners
                    </button>
                </div>
            )}

            {view === 'notifications' ? (
                <>
                    {/* Filter Tabs */}
                    <div className="flex gap-2.5 mb-5 mt-2">
                        {['all', 'unread', 'read'].map(f => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`flex items-center gap-2 px-4 py-2 text-[13px] font-bold rounded-full transition-all duration-200 ${
                                    filter === f
                                        ? 'bg-[#1a3884] text-white shadow-md shadow-[#1a3884]/20 dark:bg-[#4f7dff] dark:text-[#00152E]'
                                        : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 dark:bg-[#002147] dark:border-[#1a3884]/30 dark:text-gray-300 dark:hover:bg-[#1a3884]/20'
                                }`}
                            >
                                {f.charAt(0).toUpperCase() + f.slice(1)}
                                {f === 'unread' && unreadCount > 0 && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                                        filter === f 
                                        ? 'bg-white/20 text-white' 
                                        : 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400'
                                    }`}>
                                        {unreadCount}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

            {/* List */}
            <div className="space-y-3">
                {loading ? (
                    <div className="space-y-3">
                        {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                        <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-gray-500 font-medium">No notifications found</h3>
                    </div>
                ) : (
                    notifications.map((notification) => (
                        <div
                            key={notification._id}
                            onClick={() => handleNotificationClick(notification)}
                            className={`relative group bg-white p-4 rounded-xl border transition-all cursor-pointer hover:bg-gray-50/50 ${
                                notification.read ? 'border-gray-100' : 'border-indigo-100 bg-indigo-50/10 shadow-sm'
                            }`}
                        >


                            {/* Unread Indicator Strip */}
                            {!notification.read && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4f7dff]" />}
                            
                            <div className="flex gap-4 items-start pl-1 sm:pl-2">
                                <div className={`p-2.5 rounded-xl flex-shrink-0 mt-0.5 ${isDark ? 'bg-[#1a3884]/20' : 'bg-gray-50 border border-gray-100'}`}>
                                    {getIcon(notification.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-start gap-4">
                                        <h3 className={`text-[15px] font-bold leading-tight ${notification.read ? 'text-gray-600 dark:text-gray-300' : 'text-gray-900 dark:text-white'}`}>
                                            {notification.title}
                                            {!notification.read && (
                                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-black bg-blue-50 border border-blue-100 text-[#4f7dff] dark:bg-[#4f7dff]/10 dark:border-[#4f7dff]/20 dark:text-[#4f7dff] uppercase tracking-wider align-middle mb-0.5">
                                                    New
                                                </span>
                                            )}
                                        </h3>
                                        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 whitespace-nowrap bg-gray-50 dark:bg-[#1a3884]/20 px-2 py-1 rounded-md border border-gray-100 dark:border-transparent">
                                            <Clock className="w-3 h-3" />

                                            {getTimeAgo(notification.createdAt)}
                                        </div>
                                    </div>

                                    <p className="text-[13.5px] text-gray-500 dark:text-gray-400 mt-1.5 leading-relaxed pr-8">
                                        {notification.message}
                                    </p>

                                    {notification.link && (
                                        <div className="mt-2.5">
                                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                                                View details <ExternalLink className="w-3 h-3 ml-1 inline" />
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Hover Actions */}
                            <div className={`absolute top-3.5 right-3.5 opacity-0 group-hover:opacity-100 transition-all duration-200 translate-x-2 group-hover:translate-x-0 flex flex-row gap-1 p-1 rounded-lg backdrop-blur-md border shadow-sm z-10 ${isDark ? 'bg-[#002147]/95 border-[#1a3884]/40' : 'bg-white/95 border-gray-200'}`}>
                                {!notification.read && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            markAsRead(notification._id);
                                        }}
                                        className="p-1 hover:bg-gray-100 rounded text-indigo-600"
                                        title="Mark as read"
                                    >
                                        <Check className="w-4 h-4" />
                                    </button>
                                )}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        deleteNotification(notification._id);
                                    }}
                                    className="p-1 hover:bg-red-50 rounded text-red-500"
                                    title="Delete"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))
                )}

                {/* Pagination Controls */}
                {!loading && notifications.length > 0 && (
                    <div className={`rounded-xl border p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4 mt-6 transition-all duration-300 ${isDark ? 'bg-[#002147] border-[#1a3884]/40' : 'bg-white border-gray-100'}`}>
                        {/* Results Per Page */}
                        <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
                            <span>Results per page:</span>
                            <select
                                value={pagination.limit}
                                onChange={(e) => handleLimitChange(e.target.value)}
                                className="border border-gray-200 rounded-lg bg-gray-50 px-2 py-1.5 focus:ring-2 focus:ring-[#1a3884] focus:border-transparent outline-none transition-all cursor-pointer font-medium"
                            >
                                {[10, 25, 50].map(limit => (
                                    <option key={limit} value={limit}>{limit}</option>
                                ))}
                            </select>
                        </div>

                            {/* Status Info */}
                            <div className="text-sm text-gray-600 font-medium">
                                Showing <span className="font-bold text-gray-900">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                                <span className="font-bold text-gray-900">
                                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                                </span>{' '}
                                of <span className="font-bold text-gray-900">{pagination.total}</span> records
                            </div>
                        {/* Navigation Buttons */}
                        {pagination.totalPages > 1 && (
                            <nav className="flex items-center space-x-1" aria-label="Pagination">
                                <button
                                    onClick={() => handlePageChange(1)}
                                    disabled={pagination.page === 1}
                                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all hover:border-indigo-200 group"
                                    title="First Page"
                                >
                                    <div className="flex -space-x-1">
                                        <ChevronLeft className="w-5 h-5 text-gray-600 group-hover:text-indigo-600" />
                                        <ChevronLeft className="w-5 h-5 -ml-3 text-gray-600 group-hover:text-indigo-600" />
                                    </div>
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all hover:border-indigo-200 group"
                                    title="Previous Page"
                                >
                                    <ChevronLeft className="w-5 h-5 text-gray-600 group-hover:text-indigo-600" />
                                </button>

                                {/* Page Numbers */}
                                <div className="hidden sm:flex items-center space-x-1 mx-2">
                                    {[...Array(pagination.totalPages)].map((_, idx) => {
                                        const pageNum = idx + 1;
                                        if (
                                            pageNum === 1 || 
                                            pageNum === pagination.totalPages || 
                                            (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => handlePageChange(pageNum)}
                                                    className={`w-9 h-9 rounded-lg text-sm font-bold transition-all duration-300 ${
                                                        pagination.page === pageNum
                                                            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 scale-110'
                                                            : 'border border-gray-200 text-gray-600 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        }
                                        if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                                            return <span key={pageNum} className="px-1 text-gray-400 font-bold">...</span>;
                                        }
                                        return null;
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page === pagination.totalPages}
                                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all hover:border-indigo-200 group"
                                    title="Next Page"
                                >
                                    <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-indigo-600" />
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                    disabled={pagination.page === pagination.totalPages}
                                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all hover:border-indigo-200 group"
                                    title="Last Page"
                                >
                                    <div className="flex -space-x-1">
                                        <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-indigo-600" />
                                        <ChevronRight className="w-5 h-5 -ml-3 text-gray-600 group-hover:text-indigo-600" />
                                    </div>
                                </button>
                            </nav>
                        )}
                    </div>
                )}
            </div>
            </>
            ) : (
                <div className="space-y-4 pt-2">
                    {loadingBanners ? (
                        <div className="space-y-3">
                            {[1, 2].map(i => <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />)}
                        </div>
                    ) : banners.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                            <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-gray-500 font-medium">No banners found</h3>
                            <p className="text-sm text-gray-400 mt-2">Click Add Banner to create one.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {banners.map(banner => (
                                <div key={banner._id} className="bg-white dark:bg-[#002147] rounded-xl border border-gray-200 dark:border-[#1a3884]/20 overflow-hidden group shadow-sm hover:shadow transition-shadow">
                                    <div className="h-40 bg-gray-100 dark:bg-[#001f40] relative">
                                        <img src={getFileUrl(banner.image)} alt="Banner" className="w-full h-full object-cover" />
                                        <div className={`absolute top-2 right-2 px-2.5 py-1 text-xs font-semibold rounded-full border shadow-sm backdrop-blur-md ${banner.isActive ? 'bg-[#1a3884]/15 border-[#1a3884]/20 text-[#1a3884] dark:bg-[#4f7dff]/15 dark:border-[#4f7dff]/20 dark:text-[#4f7dff]' : 'bg-gray-100/90 border-gray-200 text-gray-700 dark:bg-gray-800/90 dark:border-gray-700 dark:text-gray-300'}`}>
                                            {banner.isActive ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>
                                    <div className="p-4 flex flex-col justify-between">
                                        <p className="text-sm text-gray-700 dark:text-gray-250 mb-4 line-clamp-2 h-10">
                                            {banner.message || <span className="text-gray-400 dark:text-gray-500 italic">No message provided</span>}
                                        </p>
                                        <div className="flex justify-between items-center border-t border-gray-100 dark:border-[#1a3884]/20 pt-3 mt-auto">
                                            <button
                                                onClick={() => toggleBannerStatus(banner._id, banner.isActive)}
                                                className={`text-sm font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${banner.isActive ? 'text-[#1a3884] dark:text-[#4f7dff] hover:bg-[#1a3884]/10 dark:hover:bg-[#4f7dff]/10 hover:text-[#132A55] dark:hover:text-white' : 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/20 hover:text-green-700 dark:hover:text-green-300'}`}
                                            >
                                                <Power className="w-4 h-4" />
                                                {banner.isActive ? 'Deactivate' : 'Activate'}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteBanner(banner._id)}
                                                className="text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 hover:text-red-700 flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" /> Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Broadcast Modal */}
            <Modal
                isOpen={showBroadcastModal}
                onClose={() => setShowBroadcastModal(false)}
                title="Send Broadcast Notification"
                size="lg"
            >
                <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1 custom-scrollbar">
                    <Select
                        label="Target Audience"
                        value={broadcastData.targetRole}
                        onChange={(e) => setBroadcastData({ ...broadcastData, targetRole: e.target.value })}
                        options={getTargetOptions()}
                    />

                    <Select
                        label="Type"
                        value={broadcastData.type}
                        onChange={(e) => setBroadcastData({ ...broadcastData, type: e.target.value })}
                        options={[
                            { value: 'info', label: 'Information (Blue)' },
                            { value: 'success', label: 'Success (Green)' },
                            { value: 'warning', label: 'Warning (Yellow)' },
                            { value: 'error', label: 'Alert (Red)' }
                        ]}
                    />

                    <Input
                        label="Title"
                        placeholder="e.g. System Maintenance"
                        value={broadcastData.title}
                        onChange={(e) => setBroadcastData({ ...broadcastData, title: e.target.value })}
                    />

                    <Textarea
                        label="Message"
                        placeholder="Type your announcement here..."
                        rows={4}
                        value={broadcastData.message}
                        onChange={(e) => setBroadcastData({ ...broadcastData, message: e.target.value })}
                    />

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            variant="outline"
                            onClick={() => setShowBroadcastModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleBroadcastSubmit}
                            loading={sending}
                            icon={Send}
                        >
                            Send Notification
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Banner Modal */}
            <Modal
                isOpen={showBannerModal}
                onClose={() => setShowBannerModal(false)}
                title="Add Institute Banner"
                size="md"
            >
                <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1 custom-scrollbar">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Banner Image</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setBannerData({ ...bannerData, image: e.target.files[0] })}
                            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                        />
                    </div>

                    <Textarea
                        label="Banner Message (Optional)"
                        placeholder="Type a message to be displayed with the banner..."
                        rows={3}
                        value={bannerData.message}
                        onChange={(e) => setBannerData({ ...bannerData, message: e.target.value })}
                    />

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            variant="outline"
                            onClick={() => setShowBannerModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleBannerSubmit}
                            loading={savingBanner}
                            icon={ImageIcon}
                        >
                            Save Banner
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Notifications;
